<script lang="ts">
  import Chat from "./components/Widget/Chat.svelte";
  import ChatClose from "./components/Widget/ChatClose.svelte";
  import PreviewBubble from "./components/Widget/PreviewBubble.svelte";
  import styles from "./components/Widget/styles";
  import LogoSingleIconOnly from "./components/icons/logo-single-icon-only.svelte";

  import { getChatSettings, getCart, getWidgetIcon } from "./lib";
  import { ChatHistoryStore } from "./lib/stores/widget";
  import { calculateFontColor, darkenColor } from "./lib/color";
  import { onMount, onDestroy } from "svelte";
  import L from "./lib/locales";
  import moment from "moment-timezone";
  import "moment/min/locales";
  import {
    currentDateInStoreTimezone,
    dayOfWeekMap,
    findNextOpenDayOffset,
    calculateDaysFrom,
    constructDateFromTimezone,
    formatToCustomerLocale,
    currentTimeInStoreTimezone,
  } from "./lib/time";

  const sessionCookieName = "_tv_wid_sess";

  let showRedCircle = false;
  let chatHidden = true;
  let webSocket: WebSocket | null = null;
  let history: any[] = [];
  let lastBotOrAgentMessageText: string | null = null;
  let lastBotOrAgentMessageCreatedAt: Date | null = null;
  let detectedLanguage: string | null = null;

  const s = styles();
  let settings: any = null;
  let cartJs: any = null;
  let originalDocumentTitle: string | null = null;
  let isOnline: boolean | null = null;
  let nextOnlineTime: string | null = null;
  let lastSeenAgentResponse: any = null;
  let tempMessage: any = null;
  let widgetIconUrl: string | null = null;
  let loading = false;
  let agentTyping = false;

  function connect() {
    // Get the account info first before connecting to websocket
    getChatSettings(
      cartJs,
      `${sessionCookieName}=${localStorage.getItem(sessionCookieName) || ""}`,
    ).then((newSettings) => {
      settings = newSettings;

      // Set the session cookie
      localStorage.setItem(sessionCookieName, settings.session_id);

      // HACK: Safari doesn't send cookie when connecting to a websocket endpoint. We pass it as a query parameter.
      webSocket = new WebSocket(
        `${process.env.TALKVISOR_WS_URL}/ws?${sessionCookieName}=${settings.session_id}`,
      );
      webSocket.onerror = (err) => {
        console.error("Websocket error:", err);

        // Clear session key.
        localStorage.removeItem(sessionCookieName);

        try {
          // This triggers 'onclose'.
          webSocket?.close();
        } catch (e) {
          // If closing failed, manually reconnect.
          console.log(e, "Reconnecting...");
          setTimeout(() => connect(), 1000);
        }
      };
      webSocket.onclose = () => {
        console.error("Websocket closed. Reconnecting...");
        setTimeout(() => connect(), 1000);
      };
      webSocket.onmessage = (event) => {
        const res = JSON.parse(event.data);

        // If message is generating an AI message, show loading wave
        if (res.type === "message_generating") {
          loading = true;
          // If loading takes too long, stop showing loading wave
          setTimeout(() => {
            loading = false;
          }, 20000);
          return;
        }
        loading = false;

        // If agent is typing, show typing indicator
        if (res.type === "agent_typing") {
          agentTyping = true;
          setTimeout(() => {
            agentTyping = false;
          }, 20000);
          return;
        }

        if (res.type === "agent_typing_stopped") {
          agentTyping = false;
        }

        // Duplicate bot message check
        const last =
          history.length > 0 ? history[history.length - 1] : undefined;
        if (
          last &&
          res.type === "bot_message" &&
          res.type === last.type &&
          res.payload.text === last.payload.text
        ) {
          return;
        }

        // Remove any messages that were already added as a temp message
        if (
          tempMessage &&
          res.type === tempMessage?.type &&
          res.payload.text === tempMessage?.payload?.text
        ) {
          history = history.filter(
            (a) =>
              !(a.type === res.type && a.payload.text === res.payload.text),
          );
          tempMessage = null;
        }

        // Reset the last unread bot or agent message text
        lastBotOrAgentMessageText = null;
        lastBotOrAgentMessageCreatedAt = null;

        // TODO: Use sorted set
        if (
          !history.find(
            (a) => a.type === res.type && a.created_at === res.created_at,
          )
        ) {
          history.push(res);

          if (
            res.type === "agent_message" &&
            res?.actor_id &&
            lastSeenAgentResponse?.actor_id !== res?.actor_id
          ) {
            history.push({
              type: "new_agent_announcement",
              actor_full_name: res?.actor_full_name,
              payload: res?.payload,
              actor_id: res?.actor_id,
              actor_has_thumbnail: res?.actor_has_thumbnail,
              created_at: res?.created_at,
            });
            lastSeenAgentResponse = res;
          }

          if (!chatHidden) setLastRead();
        }

        history.sort((a, b) => {
          const aa = new Date(a.created_at);
          const bb = new Date(b.created_at);
          if (aa > bb) return 1;
          if (aa < bb) return -1;
          return 1;
        });

        ChatHistoryStore.set(history as any);
        if (chatHidden) setRedCircle();

        const lastHistoryMessage = history[history.length - 1];
        detectedLanguage = lastHistoryMessage?.language || language;
        if (
          isUnread() &&
          (lastHistoryMessage.type === "bot_message" ||
            lastHistoryMessage.type === "agent_message" ||
            lastHistoryMessage.type === "new_agent_announcement")
        ) {
          lastBotOrAgentMessageText =
            res.payload?.translations?.[detectedLanguage || "English"] ||
            res.payload.text;
          lastBotOrAgentMessageCreatedAt = new Date(res.created_at);

          if (!originalDocumentTitle) {
            originalDocumentTitle = document.title;

            document.title = "A new message has arrived.";

            setTimeout(() => {
              document.title = originalDocumentTitle || document.title;
            }, 1000);

            setTimeout(() => {
              document.title = "A new message has arrived.";
            }, 2000);

            setTimeout(() => {
              document.title = originalDocumentTitle || document.title;
            }, 3000);

            setTimeout(() => {
              document.title = "A new message has arrived.";
            }, 4000);

            setTimeout(() => {
              document.title = originalDocumentTitle || document.title;
              originalDocumentTitle = null;
            }, 5000);
          }
        }
      };
      webSocket.onopen = () => {
        console.log("Websocket connected.");
        try {
          ping();
          setPresetInfo();
        } catch (exception) {
          console.error("Error sending beacon", exception);
        }
      };

      setTimeout(() => {
        try {
          setOnlineStatus();
        } catch (e) {
          console.error("Error setting online status", e);
        }
      }, 0);
    });
  }

  function setLastRead() {
    const date = new Date();
    localStorage.setItem("lastRead", date.toISOString());
  }

  function setPresetInfo() {
    const {
      tv_preset_customer_first_name,
      tv_preset_customer_last_name,
      tv_preset_customer_email,
    } = (window as any).Shopify || {};

    if (
      tv_preset_customer_email &&
      tv_preset_customer_first_name &&
      tv_preset_customer_last_name
    ) {
      if (webSocket) {
        webSocket.send(
          JSON.stringify({
            email: tv_preset_customer_email,
            first_name: tv_preset_customer_first_name,
            last_name: tv_preset_customer_last_name,
          }),
        );
      }
    }
  }

  function getElementDisplayName(el: HTMLElement | null): string | undefined {
    let text: string | undefined;
    while (el) {
      if (["A", "BUTTON"].indexOf(el.nodeName) >= 0) {
        if (el.innerText) {
          text = el.innerText;
        } else if (el.getAttribute("alt")) {
          text = el.getAttribute("alt") || undefined;
        } else if (el.getAttribute("title")) {
          text = el.getAttribute("title") || undefined;
        }

        if (text) break;
      }
      el = el.parentElement;
    }

    if (text && typeof text === "string") {
      text = text.split("\n")[0];
      if (text.length > 32) {
        text = text.slice(0, 32) + "...";
      }
    }

    return text;
  }

  function isUnread() {
    const lastRead = localStorage.getItem("lastRead");
    const lastMessageTime = history[history.length - 1].created_at;
    return lastRead ? new Date(lastRead) < new Date(lastMessageTime) : true;
  }

  function setRedCircle() {
    if (isUnread()) showRedCircle = true;
  }

  function toggleChat() {
    chatHidden = !chatHidden;
    showRedCircle = false;
    if (!chatHidden) setLastRead();
  }

  function onMessage(event: CustomEvent) {
    const { detail: message } = event;
    if (webSocket) {
      webSocket.send(
        JSON.stringify({
          url: location.pathname,
          shopify_cart_id: cartJs?.token,
          message,
        }),
      );
    }

    tempMessage = {
      type: "widget_message",
      payload: {
        text: message,
        language: null,
        translations: null,
        in_reply_to_id: null,
      },
      actor_id: null,
      actor_full_name: null,
      actor_has_thumbnail: null,
      created_at: new Date().toISOString(),
      temp_message: true,
    };
    ChatHistoryStore.update((h: any[]) => {
      h.push(tempMessage);
      return h as any;
    });
  }

  function onFaq(event: CustomEvent) {
    const { question, answer } = event.detail;

    const questionMessage = {
      type: "widget_message",
      payload: {
        text: question,
        language: null,
        translations: null,
        in_reply_to_id: null,
      },
      actor_id: null,
      actor_full_name: null,
      actor_has_thumbnail: null,
      created_at: new Date().toISOString(),
    };
    const answerMessage = {
      type: "bot_message",
      payload: {
        text: answer,
        language: null,
        translations: null,
        in_reply_to_id: null,
      },
      actor_id: null,
      actor_full_name: null,
      actor_has_thumbnail: null,
      created_at: new Date().toISOString(),
    };
    ChatHistoryStore.update((h: any[]) => {
      h.push(questionMessage);
      return h as any;
    });

    // Send answer slightly later
    setTimeout(() => {
      ChatHistoryStore.update((h: any[]) => {
        h.push(answerMessage);
        return h as any;
      });
    }, 2000);
  }

  function sendAttachment(event: CustomEvent) {
    const { detail: attachment } = event;

    if (webSocket) {
      webSocket.send(
        JSON.stringify({
          url: location.pathname,
          shopify_cart_id: cartJs?.token,
          attachment,
        }),
      );
    }
  }

  function onEmail(event: CustomEvent) {
    const { detail: email } = event;
    if (webSocket) {
      webSocket.send(JSON.stringify({ email }));
    }
  }

  function onTyping(event: CustomEvent) {
    const { detail: showIndicator } = event;
    if (webSocket) {
      webSocket.send(
        JSON.stringify({
          url: location.pathname,
          shopify_cart_id: cartJs?.token,
          typing: showIndicator,
        }),
      );
    }
  }

  function sendBeacon(elementText: string | undefined = undefined) {
    if (!webSocket || webSocket?.readyState !== WebSocket.OPEN) return;

    webSocket?.send(
      JSON.stringify({
        url: location.pathname,
        shopify_cart_id: cartJs?.token,
        element_text: elementText,
      }),
    );
  }

  function ping() {
    if (webSocket?.readyState === WebSocket.CLOSED) {
      connect();
      return;
    }
    sendBeacon();
  }

  async function refreshCartID() {
    const token = await getCart();
    cartJs = token;
  }

  async function onClick(e: MouseEvent) {
    const name = getElementDisplayName(e.target as HTMLElement);
    if (name) sendBeacon(name);

    await refreshCartID();
    ping();
  }

  function setOnlineStatus() {
    // Set online status if available. If hours are available,
    // compute next online time. Also, compute if store should be
    // open compared to customer's locale and set online status.
    if (!settings) return;

    if (!settings.hours) {
      isOnline = settings.is_online;
      nextOnlineTime = null;
      return;
    }

    const currentDate = new Date(currentDateInStoreTimezone(settings.timezone));
    const currentDay = currentDate.getDay();
    const dayOfWeek = dayOfWeekMap[currentDay as keyof typeof dayOfWeekMap];
    const timeLowerBound = settings.hours[`${dayOfWeek}_start`];
    const timeUpperBound = settings.hours[`${dayOfWeek}_end`];

    const currentTime = currentTimeInStoreTimezone(settings.timezone);

    if (timeLowerBound && currentTime < timeLowerBound) {
      isOnline = false;
      nextOnlineTime = formatToCustomerLocale(
        constructDateFromTimezone(
          currentDate,
          settings.hours[`${dayOfWeek}_start`],
          settings.timezone,
        ),
      );
    } else if (
      timeLowerBound &&
      timeUpperBound &&
      currentTime >= timeLowerBound &&
      currentTime <= timeUpperBound
    ) {
      isOnline = true;
      nextOnlineTime = null;
    } else if (
      !timeLowerBound ||
      !timeUpperBound ||
      currentTime > timeUpperBound
    ) {
      const nextOpenDayOffset = findNextOpenDayOffset(
        currentDay,
        settings.hours,
      );

      // If we couldn't find a next open day.
      if (!nextOpenDayOffset) {
        isOnline = false;
        nextOnlineTime = null;
        return;
      }

      const nextDay = (currentDay + nextOpenDayOffset) % 7;
      const nextDayOfWeek = dayOfWeekMap[nextDay as keyof typeof dayOfWeekMap];
      const nextOpenDate = formatToCustomerLocale(
        constructDateFromTimezone(
          calculateDaysFrom(currentDate, nextOpenDayOffset),
          settings.hours[`${nextDayOfWeek}_start`],
          settings.timezone,
        ),
      );

      isOnline = false;
      nextOnlineTime = nextOpenDate;
    }
  }

  // If it's a single page app, we need to capture the cart ID modified by Shopify's JS
  window.addEventListener("click", onClick);

  // If it's a single page app, we need to capture this event as a URL changing event.
  window.addEventListener("popstate", ping);

  // Cloudflare limits the websocket idle time to 100s.
  // Sending a ping every 60s to get around this.
  const pingSender = setInterval(() => {
    if (webSocket) {
      ping();
    }
  }, 60000 /* 1 min */);
  onDestroy(() => {
    clearInterval(pingSender);
    window.removeEventListener("popstate", ping);
  });

  refreshCartID().then(() => {
    connect();
  });

  async function loadFonts(url: string) {
    var fileref = document.createElement("link");
    fileref.setAttribute("rel", "stylesheet");
    fileref.setAttribute("type", "text/css");
    fileref.setAttribute("href", url);

    // Fonts need to be added to the main DOM.
    document.getElementsByTagName("head")[0].appendChild(fileref);
  }

  async function setWidgetIcon(hasCustomIcon: boolean): Promise<void> {
    if (hasCustomIcon) {
      try {
        widgetIconUrl = await getWidgetIcon(settings?.account_id);
      } catch (error) {
        widgetIconUrl = null;
      }
    }
  }

  onMount(async () => {
    await loadFonts(
      "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800;9..40,900&family=Inter:wght@300;400;500;600;700;800;900&display=swap",
    );
  });

  let widgetPosition,
    widgetIconPattern,
    buttonBackgroundColor,
    buttonBackgroundSecondaryColor;
  $: widgetPosition = settings?.website_details?.widget_position || "right";
  $: buttonBackgroundColor =
    settings?.website_details?.button_background_color || "#3361FF";
  $: buttonBackgroundSecondaryColor =
    settings?.website_details?.button_background_secondary_color || "#C447FF";
  $: widgetIconPattern =
    settings?.website_details?.widget_icon_pattern || "solid";

  let language: string = "English";
  $: {
    language = detectedLanguage || settings?.language || "English";
    moment.locale(L[language] || "en");
  }
  $: setWidgetIcon(!!settings?.website_details?.widget_icon_url);
</script>

<main>
  <div
    class={s.positioner}
    style={`${widgetPosition}: ${widgetPosition === "right" ? "-10px" : "20px"};`}
  >
    {#if settings && webSocket}
      <!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
      <div class={chatHidden ? s.chatIcon : s.hidden} on:click={toggleChat}>
        {#if widgetIconUrl}
          <img class={s.widgetIcon} src={widgetIconUrl} alt="" />
        {:else}
          <LogoSingleIconOnly
            width="64"
            mainGradientColor={buttonBackgroundColor}
            secondGradientColor={widgetIconPattern === "solid"
              ? darkenColor(buttonBackgroundColor)
              : buttonBackgroundSecondaryColor}
          />
        {/if}
      </div>
      <!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
      <div class={chatHidden ? s.hidden : s.chatIcon} on:click={toggleChat}>
        <ChatClose color={buttonBackgroundColor} />
      </div>
      <PreviewBubble
        text={lastBotOrAgentMessageText || ""}
        createdAt={lastBotOrAgentMessageCreatedAt || new Date()}
        bind:chatHidden
        color={buttonBackgroundColor}
        fontColor={calculateFontColor(buttonBackgroundColor)}
        position={widgetPosition}
        {language}
      />
      <Chat
        {settings}
        {language}
        bind:chatHidden
        on:attachment={sendAttachment}
        on:message={onMessage}
        on:email={onEmail}
        on:faq={onFaq}
        on:typing={onTyping}
        fontColor={calculateFontColor(buttonBackgroundColor)}
        gradientColor={widgetIconPattern === "gradient"
          ? buttonBackgroundSecondaryColor
          : null}
        mainColor={buttonBackgroundColor}
        {isOnline}
        {nextOnlineTime}
        position={widgetPosition}
        {loading}
        {agentTyping}
      />
    {/if}
  </div>
</main>
